<template>
  <div class="d-flex justify-content-center drop-container bg-white">
    <div :id="id" class="dropzone w-100">
      <div class="img-circle rounded-circle dz-message">
        <i :class="icon" />
      </div>
      <p class="text-uppercase font-weight-bold dz-message">{{ $t('dropzone:drop') }}</p>
    </div>
  </div>
</template>

<script>
import { upperFirst } from 'lodash-es'
import Dropzone from 'dropzone'
import constants from '@/configuration/constants'

// Disabling autoDiscover, otherwise Dropzone will try to attach twice.
Dropzone.autoDiscover = false

export default {
  props: {
    // Id to...identify (generate a random Id if not provided)
    id: {
      type: String,
      required: true
    },
    // Url to send file
    url: {
      type: String,
      required: true
    },
    // Icon class: icon-file-txt
    icon: {
      type: String,
      required: true
    },
    // Accepted extensions, separated by ',' : '.tar.gz, .tgz'
    acceptedFiles: {
      type: String,
      required: true
    },
    // Max nb files
    maxFiles: {
      type: Number,
      default: 1
    },
    // Max file size (mb)
    maxFilesize: {
      type: Number,
      default: constants.gatewaySoftwareUpdateMaxFilesizeMB
    },
    // Upload timeout (ms)
    timeout: {
      type: Number,
      default: constants.requestTimeout
    },
    // Boolean to start upload by parent component
    // Will be reset to false here, so do not forget the .sync in parent
    startUpload: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  data () {
    return {
      dropzone: undefined,
      hasTimeoutError: false
    }
  },

  watch: {
    // Start upload when boolean set to true
    startUpload (startUpload) {
      if (startUpload) {
        this.dropzone.processQueue()
      }
    }
  },

  mounted () {
    const element = document.getElementById(this.id)

    this.dropzone = new Dropzone(element, {
      url: () => this.url,
      maxFiles: this.maxFiles,
      maxFilesize: this.maxFilesize,
      timeout: this.timeout,
      addRemoveLinks: true,
      acceptedFiles: this.acceptedFiles,
      autoProcessQueue: false,
      headers: {
        // accept everything in order to the server not blocking us
        Accept: '*/*',
        Authorization: 'Bearer ' + this.$api.getLocalToken()
      },
      previewTemplate:
        `<div class="dz-preview dz-file-preview">
          <div class="dz-details">
            <span data-dz-name></span>
            <span data-dz-size></span>
            <span class="icon-check no-pointer text-success"></span>
            <span class="icon-remove no-pointer text-danger"></span>
          </div>
          <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
          <div class="dz-error-message"><span data-dz-errormessage></span></div>
        </div>`,
      dictDefaultMessage: '<br>' + upperFirst(this.$t('dropzone:dictDefaultMessage')),
      dictFileTooBig: upperFirst(this.$t('dropzone:dictFileTooBig', { maxFilesize: this.maxFilesize })),
      dictRemoveFile: upperFirst(this.$t('dropzone:dictRemoveFile')),
      dictCancelUpload: upperFirst(this.$t('dropzone:dictCancelUpload')),
      dictCancelUploadConfirmation: upperFirst(this.$t('dropzone:dictCancelUploadConfirmation')),
      dictMaxFilesExceeded: upperFirst(this.$t('dropzone:dictMaxFilesExceeded', { maxFiles: this.maxFiles })),
      dictInvalidFileType: upperFirst(this.$t('dropzone:dictInvalidFileType', { acceptedFiles: this.acceptedFiles }))
    })

    // Handle the dropzone events
    this.dropzone.on('addedfile', (file) => {
      this.$emit('dropzone-file-added', file)
    })

    this.dropzone.on('removedfile', (file) => {
      this.$emit('dropzone-file-removed', file)
    })

    this.dropzone.on('sending', (file, xhr, formData) => {
      this.$emit('dropzone-sending', file, xhr, formData)

      // Workaround for no event on timeout: https://gitlab.com/meno/dropzone/issues/23
      // Listen to xhr timeout to cancel request manually
      this.hasTimeoutError = false
      xhr.ontimeout = () => {
        this.hasTimeoutError = true
        this.$emit('dropzone-error', file, 'Upload timed out.', xhr)
      }
    })

    this.dropzone.on('success', (file, response) => {
      this.$emit('update:startUpload', false)
      // Remove all files
      this.dropzone.removeAllFiles()
      this.$emit('dropzone-success', file, response)
    })

    this.dropzone.on('error', (file, error, xhr) => {
      if (!this.hasTimeoutError) {
        this.$emit('update:startUpload', false)

        // No error message when there is already a dropzone error
        if (error === upperFirst(this.$t('dropzone:dictInvalidFileType', { acceptedFiles: this.acceptedFiles }))) return
        if (error === upperFirst(this.$t('dropzone:dictFileTooBig', { maxFilesize: this.maxFilesize }))) return
        if (error === upperFirst(this.$t('dropzone:dictMaxFilesExceeded', { maxFiles: this.maxFiles }))) return

        this.$emit('dropzone-error', file, error, xhr)
      }
    })
  },

  beforeDestroy () {
    // Remove all files and destroy dropzone element
    this.dropzone.removeAllFiles()
    this.dropzone.destroy()
  }
}
</script>
